import { useEffect, useMemo, useState } from "react"
import Pagination from "@/components/pagination"
import { userStore } from "@/routes/admin/user/admin-user-store"
import { USER_COLUMNS } from "@/routes/admin/user/admin-user-const"
import ActionToolbar from "@/routes/admin/_components/action-toolbar"
import { deleteUserAPI, updateUserAPI } from "@/apis/admin-api"
import { toast } from "sonner"
import CreateUserForm from "@/routes/admin/user/_components/create-user-form"
import SearchBar from "@/routes/admin/_components/search-bar"
import TableHeader from "../_components/table/table-header"
import TableData from "../_components/table/table-data"

const AdminUserPage = () => {
  const { list, pagination, updateQuery, apiAction } = userStore()

  useEffect(() => {
    userStore.getState().apiAction()
  }, [])

  const rowGridClass = "grid grid-cols-[1fr_1.2fr_1.5fr_110px_130px_2fr_100px_110px]"

  const [selectedUser, setSelectedUser] = useState({
    user_id: "",
    user_name: ""
  })

  const [keyword, setKeyword] = useState("")

  const [changedMap, setChangedMap] = useState({})

  const [isFormOpen, setIsFormOpen] = useState(false)

  const displayList = useMemo(() => {
    return list.map((item) => ({
      ...item,
      ...changedMap[item.user_id]
    }))
  }, [list, changedMap])

  const hasPendingChanges = useMemo(() => {
    return Object.keys(changedMap).length > 0
  }, [changedMap])

  const changeField = (userId, key, value) => {
    const origin = list.find((v) => v.user_id === userId)

    if (!origin) return

    const originValue = origin[key] ?? null
    const changedValue = value ?? null

    setChangedMap((prev) => {
      const next = { ...prev }

      if (originValue === changedValue) {
        if (next[userId]) {
          delete next[userId][key]

          if (Object.keys(next[userId]).length === 0) {
            delete next[userId]
          }
        }

        return next
      }

      next[userId] = {
        ...next[userId],
        [key]: changedValue
      }

      if (key === "role" && changedValue === "ADMIN") {
        next[userId] = {
          ...next[userId],
          role: changedValue,
          scope_type: null,
          scope_code: null,
          sido_key: null
        }
      }

      return next
    })
  }

  const toggleCreateForm = () => {
    setIsFormOpen((prev) => !prev)
  }

  const rowClick = (item) => {
    setSelectedUser({
      user_id: item.user_id,
      user_name: item.user_name
    })
  }

  const updateUsers = async () => {
    const payload = Object.entries(changedMap).map(([user_id, values]) => ({
      user_id,
      ...values
    }))
    try {
      const { success } = await updateUserAPI(payload)

      if (success) {
        toast.success("수정되었습니다.")
        await apiAction()
        setChangedMap({})
      }
    } catch (err) {
      if (err.status === 400) {
        toast.error(err.response.data.detail)
      }
    }
  }

  const deleteUser = async () => {
    if (!window.confirm(`${selectedUser.user_id} 계정을 삭제하시겠습니까?`)) return

    if (!selectedUser.user_id) {
      return toast.error("선택된 아파트 없음")
    }

    const { success } = await deleteUserAPI({
      user_id: selectedUser.user_id
    })

    if (success) {
      toast.success(`${selectedUser.user_name}님의 계정이 삭제되었습니다.`)
      await apiAction()
    }
  }

  const onSearch = async () => {
    await updateQuery({ keyword })
  }

  return (
    <section className="flex-1 flex flex-col">
      <article className="flex items-center justify-between">
        <SearchBar
          placeholder={"ID, 유저명, 소속코드로 검색"}
          onChange={(e) => setKeyword(e.target.value)}
          value={keyword}
          onSubmit={onSearch}
        />

        <ActionToolbar
          onCreate={toggleCreateForm}
          isFormOpen={isFormOpen}
          hasChanged={hasPendingChanges}
          onUpdate={updateUsers}
          onDelete={deleteUser}
        />
      </article>

      {isFormOpen && <CreateUserForm successAction={() => setIsFormOpen(false)} />}

      <TableHeader className={rowGridClass} columns={USER_COLUMNS} />

      <TableData
        datalist={displayList}
        columns={USER_COLUMNS}
        rowKey={"user_id"}
        className={rowGridClass}
        selected={selectedUser}
        rowClick={rowClick}
        onChange={changeField}
      />

      <footer className="mt-auto">
        <Pagination
          currentPage={pagination.page}
          totalPages={Math.ceil(pagination.total / pagination.size)}
          onChange={(p) => updateQuery({ page: p })}
          activeStyle="bg-toicos-navy"
        />
      </footer>
    </section>
  )
}

export default AdminUserPage
